import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaFileInvoiceDollar, FaSave, FaTimes } from "react-icons/fa";

const ReceiptForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [sales, setSales] = useState([]);
  
  const [formData, setFormData] = useState({
    party_type: "Customer",
    customer_id: "",
    supplier_id: "",
    sales_id: "",
    invoice_id: "",
    amount_paid: "",
    payment_mode: "Cash",
    bank_id: "",
    cheque_number: "",
    cheque_date: "",
    tax_percentage: "0",
    tax_amount: "0",
    payment_date: new Date().toISOString().split('T')[0],
    remarks: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [custRes, suppRes, bankRes] = await Promise.all([
          api.get("customers/"),
          api.get("suppliers/"),
          api.get("banks/"),
        ]);
        setCustomers(custRes.data);
        setSuppliers(suppRes.data);
        setBanks(bankRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.party_type === "Customer" && formData.customer_id) {
      const fetchSales = async () => {
        try {
          const res = await api.get(`sales/?customer_id=${formData.customer_id}`);
          setSales(res.data);
        } catch (err) {
          console.error("Error fetching sales:", err);
        }
      };
      fetchSales();
    } else {
      setSales([]);
    }
  }, [formData.customer_id, formData.party_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      
      // Auto calculate tax
      if (name === "amount_paid" || name === "tax_percentage") {
        const amount = parseFloat(name === "amount_paid" ? value : prev.amount_paid) || 0;
        const taxPercent = parseFloat(name === "tax_percentage" ? value : prev.tax_percentage) || 0;
        updated.tax_amount = (amount * (taxPercent / 100)).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount_paid) {
      alert("Amount Paid is required");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount_paid: Number(formData.amount_paid),
        tax_percentage: Number(formData.tax_percentage),
        tax_amount: Number(formData.tax_amount),
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        sales_id: formData.sales_id ? parseInt(formData.sales_id) : null,
        invoice_id: formData.invoice_id ? parseInt(formData.invoice_id) : null,
        bank_id: formData.bank_id ? parseInt(formData.bank_id) : null,
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null,
        cheque_date: formData.cheque_date ? new Date(formData.cheque_date).toISOString() : null,
      };
      
      await api.post("receipts/", payload);
      alert("Receipt saved successfully!");
      navigate("/dashboard/receipts/list");
    } catch (err) {
      console.error("Error saving receipt:", err);
      alert("Failed to save receipt. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 max-w-2xl mx-auto">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-primary text-white d-flex align-items-center gap-2">
          <FaFileInvoiceDollar />
          <h4 className="mb-0">Add New Receipt</h4>
        </div>
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Receipt Date</label>
                <input
                  type="date"
                  name="payment_date"
                  className="form-control"
                  value={formData.payment_date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Party Type</label>
                <select
                  name="party_type"
                  className="form-select"
                  value={formData.party_type}
                  onChange={handleChange}
                >
                  <option value="Customer">Customer</option>
                  <option value="Supplier">Supplier</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">
                {formData.party_type === "Customer" ? "Select Customer" : "Select Supplier"}
              </label>
              {formData.party_type === "Customer" ? (
                <select
                  name="customer_id"
                  className="form-select"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  name="supplier_id"
                  className="form-select"
                  value={formData.supplier_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplier_name || s.name}</option>
                  ))}
                </select>
              )}
            </div>

            {formData.party_type === "Customer" && (
              <div className="mb-3">
                <label className="form-label fw-bold">Sales ID (Optional)</label>
                <select
                  name="sales_id"
                  className="form-select"
                  value={formData.sales_id}
                  onChange={handleChange}
                >
                  <option value="">-- Select Sale --</option>
                  {sales.map((s) => (
                    <option key={s.id} value={s.id}>Sale #{s.id} - {s.grand_total}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-bold">Payment Mode</label>
              <select
                name="payment_mode"
                className="form-select"
                value={formData.payment_mode}
                onChange={handleChange}
              >
                <option value="Cash">Cash</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Credit Card">Credit Card</option>
              </select>
            </div>

            {(formData.payment_mode === "Bank Transfer" || formData.payment_mode === "Credit Card") && (
              <div className="mb-3">
                <label className="form-label fw-bold">Select Bank</label>
                <select
                  name="bank_id"
                  className="form-select"
                  value={formData.bank_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Select Bank --</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}

            {formData.payment_mode === "Cheque" && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Number</label>
                  <input
                    type="text"
                    name="cheque_number"
                    className="form-control"
                    value={formData.cheque_number}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold">Cheque Date</label>
                  <input
                    type="date"
                    name="cheque_date"
                    className="form-control"
                    value={formData.cheque_date}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold text-danger">Amount Paid *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount_paid"
                  className="form-control"
                  value={formData.amount_paid}
                  onChange={handleChange}
                  required
                  placeholder="0.00"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax (%)</label>
                <input
                  type="number"
                  step="0.01"
                  name="tax_percentage"
                  className="form-control"
                  value={formData.tax_percentage}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label fw-bold">Tax Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.tax_amount}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Remarks</label>
              <textarea
                name="remarks"
                className="form-control"
                rows="3"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Additional notes..."
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-2"
                onClick={() => navigate("/dashboard/receipts/list")}
              >
                <FaTimes /> Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={loading}
              >
                <FaSave /> {loading ? "Saving..." : "Save Receipt"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReceiptForm;
